import httpStatus from 'http-status-codes';

import { selectRow, selectRowByRowIndex } from '@client-shared/ui-agGrid';

import {
  FETCH_CHART_ACCOUNTS,
  FETCH_CHART_ACCOUNTS_SUCCESS,
  FETCH_CHART_ACCOUNTS_FAILURE,
  FETCH_INITIAL_DATA_FOR_CHART_MODAL_SUCCESS,
  SAVING_CHART,
  SAVING_CHART_SUCCESS,
  SAVING_CHART_FAILURE,
  FETCH_INITIAL_DATA_FOR_CHART_ACCOUNT_MODAL_SUCCESS,
  SAVING_CHART_ACCOUNT,
  SAVING_CHART_ACCOUNT_SUCCESS,
  SAVING_CHART_ACCOUNT_FAILURE,
  SELECT_PERIOD,
  SELECT_CHART,
  FETCH_CHARTS_SUCCESS,
  FETCH_CHART_EDIT_VALIDATION_SUCCESS,
  IMPORT_CHART_OF_ACCOUNTS,
  IMPORT_CHART_OF_ACCOUNTS_FAILURE,
  IMPORT_CHART_OF_ACCOUNTS_SUCCESS,
  REMOVING_CHART_ACCOUNT_FAILURE,
  REMOVING_CHART_ACCOUNT,
  REMOVING_CHART_ACCOUNT_SUCCESS,
  REMOVING_CHART,
  REMOVING_CHART_SUCCESS,
  REMOVING_CHART_FAILURE,
  FETCH_INITIAL_DATA_FOR_CHART_ACCOUNT_MODAL,
  FETCH_INITIAL_DATA_FOR_CHART_ACCOUNT_MODAL_FAILURE,
} from './types';
import {
  fetchCharts as fetchChartsApi,
  fetchChartsByUser as fetchChartsByUserApi,
  fetchChartAccounts as fetchChartAccountsApi,
  fetchAccountCodes as fetchAccountCodesApi,
  fetchImportStatus as fetchImportStatusApi,
  addChart as addChartApi,
  updateChart as updateChartApi,
  deleteChart as deleteChartApi,
  addChartAccount as addChartAccountApi,
  updateChartAccount as updateChartAccountApi,
  deleteChartAccount as deleteChartAccountApi,
  importChart as importChartApi,
} from '../api';
import { fetchingError } from '../../../shared/store/actions';
import { errorNotification, successNotification, infoNotification } from '../../../shared/store/notificationActions';
import ModalNames from '../modalNames';
import getModalActions from '../../../shared/modals/getModalActions';
import worksheetWidthValidator from '../xlsxImport/worksheetWidth.validator';
import readXlsxFileAsJson from '../../../shared/xlsxImport/readXlsxFileAsJson';
import calculateExportRange from '../xlsxImport/calculateExportRange.utils';
import { FIELD_MAPPINGS } from '../xlsxImport/constants';
import agGridConfig from '../../../agGridConfig';
import { normalizeCellToString } from '../../../utils/readXlsxFile.utils';

const fetchCharts = period => async dispatch => {
  try {
    const response = await fetchChartsApi(period.name);

    return dispatch({
      type: FETCH_CHARTS_SUCCESS,
      chartNames: response,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const fetchChartsByUser = period => async dispatch => {
  try {
    const response = await fetchChartsByUserApi(period.name);

    return dispatch({
      type: FETCH_CHARTS_SUCCESS,
      chartNames: response,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const fetchChartsForFilter = (period, canModify) => async dispatch => {
  if (canModify) {
    dispatch(fetchCharts(period));
  } else {
    dispatch(fetchChartsByUser(period));
  }
};

export const fetchChartEditValidation = (period, chartName) => async dispatch => {
  try {
    const response = await fetchChartEditValidation(period.name, chartName.value);

    return dispatch({
      type: FETCH_CHART_EDIT_VALIDATION_SUCCESS,
      hasEditPrivilege: response,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

const fetchChartAccounts = (period, chartName) => async dispatch => {
  dispatch({ type: FETCH_CHART_ACCOUNTS });

  try {
    const chartNameValue = chartName || '-';
    const response = await fetchChartAccountsApi(period, chartNameValue);

    return dispatch({
      type: FETCH_CHART_ACCOUNTS_SUCCESS,
      chartAccounts: response,
    });
  } catch (error) {
    if (!error.isFetchingError) {
      throw error;
    }

    if (error.status === httpStatus.NOT_FOUND) {
      return dispatch({
        type: FETCH_CHART_ACCOUNTS_FAILURE,
        errorMessage: error.message,
      });
    }

    dispatch(fetchingError());
    dispatch(errorNotification(error.message));
  }
};

export const fetchInitialDataForChartOfAccounts = (period, chartName, canModify) => async dispatch => {
  await dispatch(fetchChartsForFilter(period, canModify));
  if (chartName) {
    dispatch(fetchChartAccounts(period.name, chartName.value));
  }
};

export const selectPeriod = period => dispatch => {
  dispatch({
    type: SELECT_PERIOD,
    period,
  });
};

export const selectChart = chartName => dispatch => {
  dispatch({
    type: SELECT_CHART,
    chartName,
  });
};

export const selectPeriodForChartOfAccounts = ( period, chartName, canModify ) => dispatch => {
  dispatch(selectPeriod(period));
  dispatch(fetchChartsForFilter(period, canModify));
  if (chartName) {
    return dispatch(fetchChartAccounts(period.name, chartName.value));
  }
};

export const selectChartForChartOfAccounts = ( period, chartName ) => dispatch => {
  dispatch(selectChart(chartName));
  if (chartName) {
    return dispatch(fetchChartAccounts(period.name, chartName.value));
  }
};

export const {
  showAction: showChartModalForAddEdit,
  hideAction: hideChartModalForAddEdit,
} = getModalActions(ModalNames.ChartAddEdit);

export const {
  showAction: showChartModalForImport,
  hideAction: hideChartModalForImport,
} = getModalActions(ModalNames.ChartImport);

export const {
  showAction: showChartModalForDelete,
  hideAction: hideChartModalForDelete,
} = getModalActions(ModalNames.ChartDelete);


export const {
  showAction: showChartAccountModalForAddEdit,
  hideAction: hideChartAccountModalForAddEdit,
} = getModalActions(ModalNames.ChartAccountAddEdit);


export const {
  showAction: showChartAccountModalForDelete,
  hideAction: hideChartAccountModalForDelete,
} = getModalActions(ModalNames.ChartAccountDelete);

export const fetchInitialDataForChartModal = ({
  openForEdit,
  chartName,
}) => async dispatch => {
  try {
    const chartToEditResponse = openForEdit ? { chartName: chartName.value } : null;

    return dispatch({
      type: FETCH_INITIAL_DATA_FOR_CHART_MODAL_SUCCESS,
      chartToEdit: chartToEditResponse,
    });
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};


export const saveChart = ({
  chartData,
  period,
  leaveModalOpen,
  entityToEdit,
}, openForEdit, { callback, keepOpenAfterSaving }) => async dispatch => {
  if (!period) {
    return;
  }

  dispatch({
    type: SAVING_CHART,
  });

  try {
    if (!openForEdit) {
      await addChartApi({
        periodName: period.name,
        chartData,
      });
      dispatch(successNotification('Chart added'));
    } else {
      await updateChartApi({
        periodName: period.name,
        chartData,
        entityToEdit,
      });
      dispatch(successNotification('Chart updated'));
    }

    dispatch({
      type: SAVING_CHART_SUCCESS,
    });

    if (callback) {
      callback();
    }

    await dispatch(fetchChartsForFilter(period, true));

    dispatch(selectChartForChartOfAccounts(period, {
      label: chartData.chartName,
      value: chartData.chartName,
    }));

    if (keepOpenAfterSaving) {
      return;
    }

    if (leaveModalOpen) {
      return;
    }

    dispatch(hideChartModalForAddEdit());
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      dispatch({
        type: SAVING_CHART_FAILURE,
      });
      return;
    }
    throw error;
  }
};

export const deleteChart = ({ period, chartName, canModify }) => async dispatch => {
  dispatch({ type: REMOVING_CHART });

  try {
    await deleteChartApi( period.name, chartName.value );
    dispatch(successNotification('Chart removed'));
    dispatch(hideChartModalForDelete());
    dispatch({ type: REMOVING_CHART_SUCCESS });

    dispatch(fetchChartsForFilter(period, canModify));
    return dispatch(selectChartForChartOfAccounts(period, null ));
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      dispatch({
        type: REMOVING_CHART_FAILURE,
      });
      return;
    }
    throw error;
  }
};

export const fetchInitialDataForChartAccountModal = () => async dispatch => {
  dispatch({ type: FETCH_INITIAL_DATA_FOR_CHART_ACCOUNT_MODAL });
  try {
    const accountCodesResponse = await fetchAccountCodesApi();
    const importStatusResponse = await fetchImportStatusApi();

    return dispatch({
      type: FETCH_INITIAL_DATA_FOR_CHART_ACCOUNT_MODAL_SUCCESS,
      r2rAccountValues: accountCodesResponse,
      importStatusValues: [{ longName: '', statusID: null }].concat(importStatusResponse),
    });
  } catch (error) {
    dispatch({ type: FETCH_INITIAL_DATA_FOR_CHART_ACCOUNT_MODAL_FAILURE });
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      return;
    }
    throw error;
  }
};

export const saveChartAccount = ({
  chartAccountData,
  period,
  chartName,
}, openForEdit, { keepOpenAfterSaving, callback }) => async (dispatch, getState) => {
  if (!period) {
    return;
  }

  dispatch({
    type: SAVING_CHART_ACCOUNT,
  });

  try {
    if (!openForEdit) {
      await addChartAccountApi({
        periodName: period.name,
        chartName: chartName.value,
        chartAccountData,
      });
      dispatch(successNotification('Chart Account added'));
    } else {
      await updateChartAccountApi({
        periodName: period.name,
        chartName: chartName.value,
        chartAccountData,
      });
      dispatch(successNotification('Chart Account updated'));
    }

    dispatch({
      type: SAVING_CHART_ACCOUNT_SUCCESS,
    });

    if (callback) {
      callback();
    }

    await dispatch(fetchChartAccounts(period.name, getState().administration.chartOfAccounts.chartName.value));

    if (keepOpenAfterSaving) {
      return;
    }

    dispatch(selectRow({
      name: agGridConfig.adminChartOfAccounts.name,
      fields: agGridConfig.adminChartOfAccounts.fields,
      data: chartAccountData,
    }));

    return dispatch(hideChartAccountModalForAddEdit());
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      dispatch({
        type: SAVING_CHART_ACCOUNT_FAILURE,
      });
      return;
    }
    throw error;
  }
};

export const deleteChartAccount = ({ period, chartName, localAccount, rowIndex }) => async dispatch => {
  dispatch({ type: REMOVING_CHART_ACCOUNT });

  try {
    await deleteChartAccountApi( period.name, chartName.value, localAccount );
    dispatch(successNotification('Chart Account removed'));
    dispatch(hideChartAccountModalForDelete());

    await dispatch(fetchChartAccounts(period.name, chartName.value));
    dispatch({ type: REMOVING_CHART_ACCOUNT_SUCCESS });
    return dispatch(selectRowByRowIndex(rowIndex ? rowIndex - 1 : 0));
  } catch (error) {
    if (error.isFetchingError) {
      dispatch(fetchingError());
      dispatch(errorNotification(error.message));
      dispatch({
        type: REMOVING_CHART_ACCOUNT_FAILURE,
      });
      return;
    }
    throw error;
  }
};

export const importChartOfAccounts = ({ file, periodName, chartName }) => async dispatch => {
  dispatch({ type: IMPORT_CHART_OF_ACCOUNTS });
  dispatch(infoNotification('Chart of Accounts Import Processing...'));

  try {
    const chartData = await readXlsxFileAsJson({
      file,
      prevalidators: [worksheetWidthValidator],
      getRange: calculateExportRange,
      fieldMappings: FIELD_MAPPINGS,
      fieldNormalizers: {
        account: normalizeCellToString,
      },
      mapToJsonOptions: {
        header: ['A', 'B', 'C'],
      },
    });
    await importChartApi({
      chartData,
      periodName,
      chartName,
    });

    dispatch(successNotification('Chart of Accounts Import Successful.'));
    dispatch({
      type: IMPORT_CHART_OF_ACCOUNTS_SUCCESS,
    });
  } catch (error) {
    if (error.isValidationError) {
      dispatch(errorNotification(
        error.message,
        {
          removeOnHover: false,
          showCloseButton: true,
          timeOut: 0,
        }
      ));
      return dispatch({ type: IMPORT_CHART_OF_ACCOUNTS_FAILURE });
    }
    dispatch(errorNotification(error.message));
    dispatch({ type: IMPORT_CHART_OF_ACCOUNTS_FAILURE });
  }

  dispatch(fetchChartAccounts(periodName, chartName));
};
